import React, { BaseSyntheticEvent, useEffect, useRef, useState } from 'react'
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonRow,
  IonToggle,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from '@ionic/react'
import { addCircleOutline, search, trash, trashOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { getPatientMedicationsSelector } from '../selectors'
import {
  addMedication,
  deleteMedication,
  savePatientMedications,
  setDose,
  setDoseUnits,
  setFrequency,
  setName,
  setPatientMedication,
  setRoute,
  setShowSingleSelectLookup,
  setCurrentMedication,
  setLastOralIntake,
  setPatientMedicationPNNV,
  setPatientMedicationPNNVToggle,
  setPatientMedicationsList,
} from '../store/patientMedications.actions'
import { IPatientMedication, IPatientMedications } from '../store/patientMedications.types'
import './PatientMedications.content.css'
import { isTablet } from '../../../utilities'
import MedicationSearch from '../medicationsSearch/MedicationsSearch.component'
import SInput from '../../../components/stringInput/StringInput.component'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { numberPadKeysWithDecimal } from '../../../components/numberPad/NumberPad.types'
import { getAllPNNVOptionsSelector, getPatientMedicationsOptionsSelector } from '../../options/selectors'
import Validation from '../../../components/validation/Validation.component'
import { PatientMedicationsConstants } from '../store/patientMedications.constants'
import { IOption, IPNNVOption } from '../../../common/common.types'
import { getDatapointsConfigSelector } from '../../configurations/selectors'
import DateTimePicker from '../../../components/calendar/DateTimePicker/DateTimePicker.component'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { IAlertState } from '../../../components/alert/store/alert.types'
import PNNVList from '../../../components/pnnvList/PNNVList.component'

const PatientMedicationsContent: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const isTabletDevice = isTablet()
  const patientMedicationsState = useSelector(getPatientMedicationsSelector)
  const {
    patientMedicationsList,
    showSingleSelectLookup,
    currentMedication,
    lastOralIntake,
    patientMedicationPNNV,
    __patientMedicationPNNVToggle,
  } = patientMedicationsState
  const patientMedicationsOptionsState = useSelector(getPatientMedicationsOptionsSelector)
  const { doseUnitsOptions, routeOptions, frequencyOptions } = patientMedicationsOptionsState
  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const pnnvOptions = allPNNVOptions?.['eHistory.12']
  const [flag, setFlag] = useState(true)
  const dataPointsConfig = useSelector(getDatapointsConfigSelector)
  const { DP_LAST_ORAL_INTAKE } = dataPointsConfig

  const getScrollableContent = () => {
    if (!isTabletDevice) {
      return document.getElementById('pn-medications-content') as HTMLIonContentElement
    } else {
      return document.getElementById('main-content') as HTMLIonContentElement
    }
  }

  const usePreviousMedicationsList = (patientMedicationsList: IPatientMedications) => {
    const ref: React.MutableRefObject<IPatientMedications | undefined> = useRef()
    useEffect(() => {
      ref.current = patientMedicationsList
    })
    return ref.current
  }

  const prevMedicationsList = usePreviousMedicationsList(patientMedicationsList)

  useEffect(() => {
    if (prevMedicationsList && patientMedicationsList.length > prevMedicationsList?.length) {
      getScrollableContent()?.scrollByPoint(0, 500, 500)
    }
  }, [patientMedicationsList.length, prevMedicationsList])

  const setNameOnChange = (e: CustomEvent, medication: IPatientMedication) => {
    e.preventDefault()
    // Change event is triggered when a value is selected from lookup over-riding the option id
    // set Name only when the name is different from the selected to avoid over-riding the option id
    if (!medication.name || !medication.name?.name || medication.name?.name !== e.detail.value) {
      dispatch(setName({ id: 0, name: e.detail.value }, medication, e.detail.value))
      save()
    }
  }

  const setDoseOnChange = (values: string[], medication: IPatientMedication) => {
    dispatch(setDose(values.length ? values.join('') : null, medication))
    save()
  }

  const setDoseUnitsOnChange = (option: IOption, medication: IPatientMedication) => {
    dispatch(setDoseUnits(option, medication))
    save()
  }

  const setRouteOnChange = (option: IOption, medication: IPatientMedication) => {
    dispatch(setRoute(option, medication))
    save()
  }

  const setFrequencyOnChange = (option: IOption, medication: IPatientMedication) => {
    dispatch(setFrequency(option, medication))
    save()
  }

  const onDeleteMedication = (patientMedication: IPatientMedication) => {
    dispatch(deleteMedication(patientMedication))
    save()
    dispatch(setCurrentMedication(null))
  }

  const onAddMedication = () => {
    dispatch(addMedication())
    save()
    dispatch(setCurrentMedication(null))
  }

  const save = () => {
    dispatch(savePatientMedications())
  }

  useEffect(() => {
    const content = document.getElementById(currentMedication?.key || '')
    setTimeout(() => {
      content?.scrollIntoView({
        behavior: 'smooth',
      })
    }, 100)
  }, [currentMedication?.name])

  const onLookupClick = (e: CustomEvent, patientMedication: IPatientMedication) => {
    e.preventDefault()
    dispatch(setCurrentMedication(null))
    if (isTabletDevice) {
      dispatch(setShowSingleSelectLookup(true))
    } else {
      history.push(TabConstants.PATIENT_MEDICATIONS_SEARCH)
    }
    dispatch(setPatientMedication(patientMedication))
  }

  const setLastOralIntakeChange = (value: string | null) => {
    dispatch(setLastOralIntake(value))
  }

  useEffect(() => {
    if (flag) {
      patientMedicationsList.map((medication: IPatientMedication) => {
        if (medication.name?.name && medication.name?.id === 0)
          dispatch(setName({ id: 0, name: medication.name?.name || '' }, medication, medication.name?.name || ''))
        medication.dose && dispatch(setDose(medication.dose, medication))
      })
    }
  }, [flag])

  useIonViewDidEnter(() => {
    setFlag(true)
  })

  useIonViewDidLeave(() => {
    setFlag(false)
  })

  const handlePNNVToggle = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    const isChecked = e.target.checked
    if (isChecked && ((patientMedicationsList && patientMedicationsList.length > 0) || lastOralIntake)) {
      const changeSectionWarningAlert: IAlertState = {
        header: t('patientMedications.PatientMedications'),
        message: t('pnnv.userConfirmationMessage'),
        buttons: [
          { text: t('pnnv.cancel') },
          {
            text: t('pnnv.continueAndClear'),
            role: 'destructive',
            handler: () => handleClearPreviousSelection(isChecked),
          },
        ],
      }
      dispatch(showAlert(changeSectionWarningAlert))
      dispatch(setPatientMedicationPNNVToggle(!isChecked))
      save()
    } else {
      handleClearPreviousSelection(isChecked)
    }
  }

  const handleClearPreviousSelection = (pnnvChecked: boolean) => {
    if (pnnvChecked) {
      if (patientMedicationsList && patientMedicationsList.length > 0) dispatch(setPatientMedicationsList([]))
      dispatch(setLastOralIntake(null))
    } else {
      dispatch(setPatientMedicationPNNV(null))
    }
    dispatch(setPatientMedicationPNNVToggle(pnnvChecked))
    save()
  }

  const selectOption = (option: IPNNVOption | null) => {
    dispatch(setPatientMedicationPNNV(option as IPNNVOption))
    save()
  }

  return (
    <>
      {!showSingleSelectLookup && (
        <>
          <IonItemDivider color='light' className='ion-padding-start ion-padding-top'>
            <IonLabel className='grid-item-divider-label' color='medium'>
              {t('patientMedications.addPatientMedications')}
            </IonLabel>
            <IonItem key='Toggle_PatientMedicationPNNVToggle' slot='end' lines='none' color='none'>
              <IonLabel>{t('patientMedications.PNNVToggle')}</IonLabel>
              <IonToggle
                slot='start'
                name='patientMedicationPNNVToggle'
                checked={__patientMedicationPNNVToggle}
                className='extra-padding'
                onClick={(e) => handlePNNVToggle(e)}
                color='zoll-blue-secondary'
              />
            </IonItem>
          </IonItemDivider>
          {!__patientMedicationPNNVToggle ? (
            <IonList className='list-background'>
              {patientMedicationsList.map((medication: IPatientMedication, index: number) => (
                <IonItemSliding key={medication.key}>
                  <IonCard
                    id={medication?.key}
                    className='ion-no-margin ion-margin-vertical'
                    data-testid='PatientMedications'>
                    <IonCardContent>
                      <IonGrid className='no-padding'>
                        <IonRow>
                          <IonCol>
                            {t('patientMedications.medication')} {index + 1}
                          </IonCol>
                          <IonButtons>
                            <IonButton data-testid='deleteMedications' onClick={() => onDeleteMedication(medication)}>
                              <IonIcon icon={trashOutline} />
                            </IonButton>
                          </IonButtons>
                        </IonRow>

                        <IonRow>
                          <IonCol className='picklist'>
                            <SInput
                              label={t('patientMedications.name')}
                              dataTestID='Name'
                              inputValue={medication.name?.name || ''}
                              onChangeAction={(e) => {
                                setNameOnChange(e, medication)
                              }}
                            />
                            <Validation id={PatientMedicationsConstants.SET_NAME} cardinalityKey={medication.key} />
                          </IonCol>

                          <IonCol>
                            <IonItem className='ion-align-items-end' lines='none'>
                              <IonButton
                                fill='clear'
                                data-testid='Lookup'
                                onClick={(e: any) => {
                                  onLookupClick(e, medication)
                                }}
                                color='primary-alt'
                                className='ion-no-margin'>
                                {t('patientMedications.lookup')}
                                <IonIcon slot='start' icon={search} />
                              </IonButton>
                            </IonItem>
                          </IonCol>
                        </IonRow>

                        <hr className='field-divider' />

                        <IonRow>
                          <IonCol className='picklist'>
                            <NumberPad
                              elementId={`Dose${index}`}
                              dataTestId={`Dose${index}`}
                              values={medication.dose ? medication.dose.split('') : []}
                              label={t('patientMedications.dose')}
                              validCharacters={numberPadKeysWithDecimal}
                              onNumPadClick={(values) => {
                                setDoseOnChange(values, medication)
                              }}
                              action={PatientMedicationsConstants.SET_DOSE}
                              actionKey={medication.key}
                            />
                            <Validation id={PatientMedicationsConstants.SET_DOSE} cardinalityKey={medication.key} />
                          </IonCol>

                          <IonCol className='picklist'>
                            <SingleSelect
                              dataTestID='DoseUnits'
                              options={doseUnitsOptions}
                              selectedOption={medication.doseUnits}
                              listHeader={t('patientMedications.doseUnit')}
                              label={t('patientMedications.doseUnit')}
                              action={setDoseUnitsOnChange}
                              dispatchData={medication}
                              showPNNVs={false}
                            />
                          </IonCol>
                        </IonRow>

                        <hr className='field-divider' />

                        <IonRow>
                          <IonCol className='picklist'>
                            <SingleSelect
                              label={t('patientMedications.route')}
                              listHeader='Route'
                              dataTestID='Route'
                              options={routeOptions}
                              action={setRouteOnChange}
                              selectedOption={medication.route}
                              dispatchData={medication}
                            />
                          </IonCol>

                          <IonCol className='picklist'>
                            <SingleSelect
                              label={t('patientMedications.frequency')}
                              listHeader='Frequency'
                              dataTestID='Frequency'
                              options={frequencyOptions}
                              action={setFrequencyOnChange}
                              selectedOption={medication.frequency}
                              dispatchData={medication}
                            />
                          </IonCol>
                        </IonRow>

                        <hr className='field-divider' />
                      </IonGrid>
                    </IonCardContent>
                  </IonCard>
                  <IonItemOptions side='end' className='delete-swipe'>
                    <IonItemOption
                      data-testid='DeleteIcon'
                      color='danger'
                      onDrag={() => onDeleteMedication(medication)}
                      onClick={() => onDeleteMedication(medication)}>
                      <IonIcon slot='icon-only' icon={trash} />
                    </IonItemOption>
                  </IonItemOptions>
                </IonItemSliding>
              ))}
              <IonRow className='ion-float-right'>
                <IonButton
                  data-testid='AddMedication'
                  className='transform-none'
                  fill='clear'
                  color='primary-alt'
                  onClick={() => onAddMedication()}>
                  <IonIcon icon={addCircleOutline} />
                  <IonLabel>{t('patientMedications.addMedication')}</IonLabel>
                </IonButton>
              </IonRow>
            </IonList>
          ) : (
            <>
              <PNNVList options={pnnvOptions} selectedOption={patientMedicationPNNV} onSelection={selectOption} />
              <IonItemDivider color='light' />
            </>
          )}
          {DP_LAST_ORAL_INTAKE === 1 && (
            <>
              <hr className='field-divider' />
              <IonRow>
                <IonCol className='picklist'>
                  <DateTimePicker
                    dataTestID='lastOralIntakeDateTimePicker'
                    selectedDateValue={lastOralIntake}
                    action={setLastOralIntakeChange}
                    label={t('patientMedications.lastOralIntake')}
                    disabled={__patientMedicationPNNVToggle}
                  />
                </IonCol>
              </IonRow>
            </>
          )}
        </>
      )}
      {showSingleSelectLookup && <MedicationSearch />}
    </>
  )
}

export default PatientMedicationsContent
